import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Workflow, Rocket, Building2, Lightbulb, Layers } from "lucide-react";
import PublicLayout from "@/components/PublicLayout";
import CourseCard from "@/components/CourseCard";
import { api } from "@/lib/api";

const LEARN = [
  { icon: Sparkles, title: "AI Automation", desc: "Automate repetitive business processes using AI." },
  { icon: Building2, title: "AI Business Building", desc: "Identify and build AI-powered business opportunities." },
  { icon: Workflow, title: "AI Workflows", desc: "Connect AI tools and business processes into systems." },
  { icon: Rocket, title: "AI Productivity", desc: "Use AI to save time and dramatically increase output." },
  { icon: Layers, title: "Business Systems", desc: "Repeatable systems instead of manual grind." },
  { icon: Lightbulb, title: "AI Opportunities", desc: "Practical ways to create new products, services & revenue." },
];

export default function Home() {
  const [courses, setCourses] = useState([]);
  const [settings, setSettings] = useState(null); // null = not yet loaded
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    api.get("/courses").then(({ data }) => setCourses(data));
    api.get("/site-settings").then(({ data }) => setSettings(data || {})).catch(() => setSettings({}));
    api.get("/testimonials").then(({ data }) => setTestimonials(data));
  }, []);

  const stats = (settings && settings.stats) || {};
  const settingsLoaded = settings !== null;
  const resolveImg = (u) => (u && u.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);

  return (
    <PublicLayout>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 z-0 pointer-events-none">
          <div className="absolute inset-y-0 right-0 w-2/3 opacity-95">
            {settingsLoaded && (() => {
              const raw = (settings && settings.hero_background_url) || "https://images.unsplash.com/photo-1620712943543-bcc4688e7485?auto=format&fit=crop&w=1800&q=80";
              return <img src={resolveImg(raw)} alt="" className="absolute inset-0 w-full h-full object-cover object-right transition-opacity duration-500 opacity-100" data-testid="hero-background" />;
            })()}
            <div className="absolute inset-0 bg-gradient-to-r from-[#F5EFE4] via-[#F5EFE4]/70 to-transparent" />
            <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-b from-transparent to-[#F5EFE4]" />
          </div>
          <div className="absolute -right-24 top-1/3 w-[520px] h-[520px] rounded-full bg-gold/25 blur-[120px]" />
        </div>
        <div className="container-x pt-24 md:pt-32 pb-20 md:pb-40 relative z-10">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <div className="max-w-xl">
              <div className="flex items-center gap-4 mb-8">
                <div className="h-px w-16 bg-gold/60" />
                <div className="overline !text-gold-600 whitespace-nowrap" data-testid="hero-eyebrow">AI Expert • Automation Strategist • Business Builder</div>
              </div>
              <h1 className="font-display text-5xl md:text-6xl xl:text-7xl font-black leading-[0.95] tracking-tighter" data-testid="hero-headline">
              {settingsLoaded && (() => {
                  const raw = (settings && settings.homepage_headline) || "MASTER AI. AUTOMATE YOUR BUSINESS. BUILD YOUR FUTURE.";
                  const parts = raw.split(/\s+/);
                  const half = Math.ceil(parts.length / 2);
                  const first = parts.slice(0, half).join(" ");
                  const second = parts.slice(half).join(" ");
                  return (
                    <>
                      <span className="text-ink-900">{first}</span>
                      <span className="block text-gradient mt-2">{second}</span>
                    </>
                  );
                })()}
              </h1>
              <p className="mt-8 text-lg text-ink-700 leading-relaxed max-w-lg" data-testid="hero-subheadline">
                {(settings && settings.homepage_subheadline) || "Learn how to use AI automation to build smarter businesses, create new opportunities, and scale systems that work for you."}
              </p>
              <div className="mt-10 flex flex-wrap items-center gap-4">
                <Link to="/courses" className="btn-neon" data-testid="hero-explore-cta">Explore Courses <ArrowRight className="ml-2 w-4 h-4" /></Link>
                <Link to="/about" className="btn-ghost" data-testid="hero-journey-cta">Start Your AI Journey</Link>
              </div>
            </div>
            {/* Right column reserved space — image lives in the absolute bg layer for a full-bleed feel */}
            <div className="hidden md:block h-[520px]" aria-hidden="true" />
          </div>
        </div>
      </section>

      {/* AUTHORITY */}
      <section className="container-x py-24">
        <div className="grid md:grid-cols-2 gap-14 items-center">
          <div className="relative aspect-[4/5] rounded-3xl overflow-hidden glow-ring bg-cream-100">
            {settingsLoaded && (
              <img
                src={resolveImg((settings && settings.niraj_photo_url) || "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjB0ZWNoJTIwZWR1Y2F0b3IlMjBwb3J0cmFpdHxlbnwwfHx8fDE3ODg5NDczMDh8MA&ixlib=rb-4.1.0&q=85")}
                alt="Niraj Bhaardwaj"
                className="absolute inset-0 w-full h-full object-cover transition-opacity duration-500"
                data-testid="home-niraj-photo"
              />
            )}
          </div>
          <div>
            <div className="overline mb-4">Meet the Instructor</div>
            <h2 className="font-display text-4xl md:text-5xl font-bold leading-tight tracking-tight">Learn AI from someone <span className="text-gradient">building with AI.</span></h2>
            <p className="mt-6 text-ink-700 leading-relaxed">{(settings && settings.about_long) || "Niraj Bhaardwaj helps entrepreneurs, creators, and professionals implement AI to automate operations, build scalable systems, and unlock new revenue opportunities. His work sits at the intersection of applied AI, systems thinking, and modern business building."}</p>
            <div className="mt-10 grid grid-cols-3 gap-4">
              {[["students","Students"],["businesses_helped","Businesses"],["ai_systems_built","AI Systems"],["courses","Courses"],["workshops","Workshops"]].slice(0,3).map(([k,l]) => (
                <div key={k} className="glass rounded-xl p-4" data-testid={`stat-${k}`}>
                  <div className="font-display text-2xl font-bold text-ink-900">{stats[k] || "—"}</div>
                  <div className="text-xs uppercase tracking-[0.2em] text-ink-500 mt-1">{l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* WHAT YOU'LL LEARN */}
      <section className="container-x py-24">
        <div className="max-w-3xl">
          <div className="overline mb-4">What you will learn</div>
          <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight">A complete AI operating system for your business.</h2>
        </div>
        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {LEARN.map((c) => (
            <div key={c.title} className="glass rounded-2xl p-8 hover:-translate-y-1 transition-transform" data-testid={`learn-card-${c.title.toLowerCase().replace(/ /g,'-')}`}>
              <c.icon className="w-6 h-6 text-gold" />
              <h3 className="mt-4 font-display text-2xl font-semibold">{c.title}</h3>
              <p className="mt-3 text-ink-600 leading-relaxed text-sm">{c.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* COURSES */}
      <section className="container-x py-24">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
          <div>
            <div className="overline mb-4">Curriculum</div>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight">Choose your AI advantage.</h2>
          </div>
          <Link to="/courses" className="text-gold hover:text-gold-hover text-sm" data-testid="home-view-all-courses">View all courses →</Link>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {courses.slice(0, 3).map((c) => <CourseCard key={c.course_id} course={c} />)}
        </div>
      </section>

      {/* TESTIMONIALS */}
      {testimonials.length > 0 && (
        <section className="container-x py-24">
          <div className="overline mb-4">Success stories</div>
          <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight max-w-3xl">What students say about the programs.</h2>
          <div className="mt-14 grid md:grid-cols-3 gap-6">
            {testimonials.slice(0, 3).map((t) => (
              <div key={t.testimonial_id} className="glass rounded-2xl p-6" data-testid={`home-testimonial-${t.testimonial_id}`}>
                <div className="flex items-center gap-3">
                  <img src={t.profile_image} alt={t.name} className="w-12 h-12 rounded-full object-cover" />
                  <div>
                    <div className="font-medium">{t.name}</div>
                    <div className="text-xs text-ink-500">{t.role}</div>
                  </div>
                </div>
                <p className="mt-5 text-ink-700 text-sm leading-relaxed">"{t.testimonial}"</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="container-x py-24">
        <div className="relative glass rounded-3xl p-10 md:p-16 overflow-hidden">
          <div className="absolute -top-32 -right-32 w-96 h-96 bg-gold/20 blur-3xl rounded-full" />
          <div className="relative">
            <h2 className="font-display text-4xl md:text-6xl font-black tracking-tighter max-w-4xl">
              The future belongs to people who know <span className="text-gradient">how to use AI.</span>
            </h2>
            <p className="mt-6 text-ink-700 max-w-2xl text-lg">Don't just learn AI. Learn how to turn AI into systems, opportunities, and business growth.</p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/courses" className="btn-neon" data-testid="bottom-cta">Explore courses <ArrowRight className="ml-2 w-4 h-4" /></Link>
              <Link to="/about" className="btn-ghost">Meet Niraj</Link>
            </div>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
